import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { MaterialModule } from 'src/app/core/material.module';
import { AuthService } from '../../services/auth.service';

/**
 * The top-level Terms & Conditions dialog.
 *
 * Shown once per identity, on the first sign-in a user ever makes through
 * sso-fe when sso-be reports `termsAcceptedAt: null`. Accepting records the
 * moment on the SSO user row; cancelling (by any means — Esc, backdrop,
 * programmatic close) returns `false` to the caller, which treats that as
 * "did not accept" and ends the sign-in attempt.
 *
 * Deliberately knows nothing about routing. The caller opens it, waits on
 * `afterClosed`, and resumes whatever it was going to do (route to a Lab,
 * open the picker) only when the user actually accepted. That keeps the
 * dialog portable — the same component works whether it was reached from
 * the login page or from the MFA challenge screen.
 */
@Component({
  selector: 'app-terms-dialog',
  templateUrl: './terms-dialog.component.html',
  imports: [CommonModule, MaterialModule, FormsModule],
})
export class TermsDialogComponent {
  /** Checkbox state. The Accept button stays disabled until it is true. */
  isAccepted = false;
  /** Submission in flight — guards against a double-click. */
  loading = false;

  constructor(
    private authService: AuthService,
    private dialogRef: MatDialogRef<TermsDialogComponent, boolean>,
  ) {}

  onAccept(): void {
    if (!this.isAccepted || this.loading) return;
    this.loading = true;
    this.authService.acceptTerms().subscribe({
      next: () => this.dialogRef.close(true),
      // Hold the dialog open on failure so the user can retry — releasing
      // only the busy state. No visible error message, matching the Lab
      // dialog's UI exactly; the sso-be fix keeps the normal path quiet.
      error: () => {
        this.loading = false;
      },
    });
  }

  /**
   * The template paints its own full-screen overlay over MatDialog's
   * backdrop, so a click outside the card lands here and never reaches the
   * backdrop. Treat it as the backdrop would: close without accepting.
   * Clicks inside the card bubble up with a different target and are
   * ignored, as is a click while an accept is in flight — closing then
   * would sign the user out under a request that may still record it.
   */
  onOverlayClick(event: MouseEvent): void {
    if (event.target !== event.currentTarget || this.loading) return;
    this.dialogRef.close(false);
  }
}
